/**
 * CDN entry point. Loaded as:
 *
 *   <script defer src=".../mb.iife.min.js"
 *     data-project-id="..." data-api-url="..."></script>
 *
 * Auto-inits the Analytics instance from the script tag's data-* attributes
 * and exposes a small `window.mb` API for programmatic calls.
 */
import { Analytics } from "./analytics.js";
import type {
  AnalyticsConfig,
  ConsentDecisions,
  ConsentState,
  Environment,
  PublicConsentPolicy,
} from "./types.js";

type AnyProps = Record<string, unknown>;

interface MbGlobal {
  track: (name: string, props?: AnyProps) => void;
  identify: (userId: string, traits?: AnyProps) => void;
  page: (props?: AnyProps) => void;
  reset: () => void;
  flush: () => Promise<void>;
  setConsent: (consent: Partial<ConsentState> | ConsentDecisions) => void;
  getConsent: () => ConsentDecisions;
  openConsentPreferences: () => void;
  withdrawConsent: () => void;
  getConsentPolicy: () => PublicConsentPolicy | null;
  readonly _instance: Analytics | null;
}

declare global {
  interface Window {
    mb?: MbGlobal;
  }
}

function readConfig(script: HTMLScriptElement): AnalyticsConfig | null {
  const d = script.dataset;
  if (!d.projectId || !d.apiUrl) {
    console.warn(
      "[mb] missing required script attributes — need data-project-id and data-api-url. Analytics not started.",
    );
    return null;
  }
  const cfg: AnalyticsConfig = {
    projectId: d.projectId,
    apiUrl: d.apiUrl,
  };
  if (d.environment) cfg.environment = d.environment as Environment;
  if (d.sampleRate) {
    const n = Number(d.sampleRate);
    if (Number.isFinite(n) && n >= 0 && n <= 1) cfg.sampleRate = n;
  }
  if (d.autoTrack === "false") cfg.disableAutoTracking = true;
  if (d.debug === "true") cfg.debug = true;
  // Consent opt-outs / bridges configurable from the script tag so a
  // non-JS integrator can flip them without writing init code.
  const consent: NonNullable<AnalyticsConfig["consent"]> = {};
  if (d.consentBanner === "false") consent.showBanner = false;
  if (d.consentGoogle === "true") consent.bridgeGoogleConsentMode = true;
  if (d.consentNecessaryEvents) {
    consent.necessaryEvents = d.consentNecessaryEvents.split(",").map((s) => s.trim()).filter(Boolean);
  }
  if (Object.keys(consent).length > 0) cfg.consent = consent;
  return cfg;
}

function bootstrap(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.mb && window.mb._instance) {
    console.warn("[mb] already initialized — ignoring duplicate snippet load");
    return;
  }

  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) {
    console.warn("[mb] could not locate the loading <script> tag");
    return;
  }
  const config = readConfig(script);
  if (!config) return;

  const a = new Analytics(config);
  a.init();

  const api: MbGlobal = {
    track: (name, props) => a.track(name, props),
    identify: (userId, traits) => a.identify(userId, traits),
    page: (props) => a.pageView(props),
    reset: () => a.reset(),
    flush: () => a.flush(),
    setConsent: (consent) => a.setConsent(consent),
    getConsent: () => a.getConsent(),
    openConsentPreferences: () => a.openConsentPreferences(),
    withdrawConsent: () => a.withdrawConsent(),
    getConsentPolicy: () => a.getConsentPolicy(),
    get _instance() {
      return a;
    },
  };
  window.mb = api;
}

bootstrap();
